import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import RFB from "@novnc/novnc";
import { Button } from "@/components/ui/button";
import { vncProtocols } from "@/lib/vncTunnel";
import { requestVncTicket, vncWebSocketUrl, type VncTicket } from "@/services/browserFarmApi";

/**
 * Vue écran d'un pod browserfarm.
 *
 * Ce qui s'affiche ici est un Chrome connecté à un vrai compte LinkedIn. Deux
 * conséquences pratiques :
 *
 * - le ticket vit dix minutes et nomme le pod. Il n'ouvre pas « la farm », il
 *   ouvre CET écran, maintenant. Il n'est pas rafraîchi automatiquement : une
 *   session de supervision qui se prolonge toute seule pendant des heures est
 *   exactement ce qu'on ne veut pas laisser traîner dans un onglet oublié ;
 * - la vue est interactive par défaut, parce que son usage principal est le
 *   login initial (formulaire, captcha, 2FA) que rien n'automatise. Le bouton
 *   « lecture seule » existe pour la supervision, où un clic accidentel sur le
 *   compte d'un client serait une action réelle sous son nom.
 */
export default function VncViewer({
  subscriptionId,
  onClose,
}: {
  subscriptionId: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [ticket, setTicket] = useState<VncTicket | null>(null);
  const [status, setStatus] = useState<"connecting" | "connected" | "closed" | "error">(
    "connecting",
  );
  const [error, setError] = useState<string | null>(null);
  const [viewOnly, setViewOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await requestVncTicket(subscriptionId, navigate);
        if (!cancelled) setTicket(t);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, navigate]);

  useEffect(() => {
    if (!ticket || !containerRef.current) return;
    const rfb = new RFB(containerRef.current, vncWebSocketUrl(), {
      // The ticket rides the subprotocol header, never the URL (access logs).
      wsProtocols: vncProtocols(ticket),
      shared: true,
    });
    // Le framebuffer fait 1280x800 (géométrie Xvfb du pod) ; on le met à
    // l'échelle du conteneur plutôt que de redimensionner la session distante,
    // qui changerait la taille de fenêtre que LinkedIn voit.
    rfb.scaleViewport = true;
    rfb.resizeSession = false;
    rfb.background = "#111827";
    rfbRef.current = rfb;

    const onConnect = () => setStatus("connected");
    const onDisconnect = () => setStatus("closed");
    const onSecurityFailure = (e: Event) => {
      const detail = (e as CustomEvent<{ reason?: string }>).detail;
      setError(detail?.reason ?? "connexion refusée");
      setStatus("error");
    };
    rfb.addEventListener("connect", onConnect);
    rfb.addEventListener("disconnect", onDisconnect);
    rfb.addEventListener("securityfailure", onSecurityFailure);

    return () => {
      rfb.removeEventListener("connect", onConnect);
      rfb.removeEventListener("disconnect", onDisconnect);
      rfb.removeEventListener("securityfailure", onSecurityFailure);
      try {
        rfb.disconnect();
      } catch {
        /* déjà fermé */
      }
      rfbRef.current = null;
    };
  }, [ticket]);

  useEffect(() => {
    if (rfbRef.current) rfbRef.current.viewOnly = viewOnly;
  }, [viewOnly]);

  const label =
    status === "connected"
      ? `Connecté à ${ticket?.pod_hostname ?? "?"}`
      : status === "connecting"
        ? "Connexion..."
        : status === "closed"
          ? "Session fermée"
          : "Erreur";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${
            status === "connected"
              ? "bg-green-100 text-green-800"
              : status === "error"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-700"
          }`}
        >
          {label}
        </span>
        <Button size="sm" variant="outline" onClick={() => setViewOnly((v) => !v)}>
          {viewOnly ? "Reprendre la main" : "Lecture seule"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Fermer
        </Button>
        {ticket && (
          <span className="text-xs text-gray-500">
            Ticket valable {Math.round(ticket.expires_in / 60)} min — rouvrir la vue en émet un
            nouveau.
          </span>
        )}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div
        ref={containerRef}
        className="h-[600px] w-full overflow-hidden rounded border bg-gray-900"
      />

      {status === "closed" && (
        <p className="text-xs text-gray-500">
          La session s&apos;est fermée — ticket expiré, pod redémarré, ou compte passé à un autre
          pod. Refermez et rouvrez la vue pour en obtenir un nouveau.
        </p>
      )}
    </div>
  );
}
